/**
 * @fileoverview API service and hooks for the "Missing Cost" review view.
 *
 * Surfaces variants whose cost has not been set (backend treats original_cost = 0
 * as unset) so the user can enter a cost inline. Reuses the existing
 * PUT /api/variants/{id} endpoint for editing.
 *
 * @module features/sku-alias/api/missingCost
 */

import { z } from 'zod'
import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query'
import apiClient from '@/lib/apiClient'
import {
  variantSchema,
  updateVariant,
  type Variant,
  type UpdateVariantPayload,
} from '@/lib/variantService'

// ============================================================================
// Schemas
// ============================================================================

/** Nested page metadata from the Spring Boot 4 Page response. */
const pageMetadataSchema = z.object({
  size: z.coerce.number().default(0),
  number: z.coerce.number().default(0),
  totalElements: z.coerce.number().default(0),
  totalPages: z.coerce.number().default(1),
})

/**
 * Paginated variants response. Spring Boot 4 nests pagination under a `page`
 * object, so we normalize it to a flat shape the table can consume.
 */
export const paginatedVariantsSchema = z
  .object({
    content: z.array(variantSchema),
    page: pageMetadataSchema.optional(),
  })
  .passthrough()
  .transform(data => ({
    content: data.content,
    totalElements: data.page?.totalElements ?? 0,
    totalPages: data.page?.totalPages ?? 1,
    size: data.page?.size ?? 0,
    number: data.page?.number ?? 0,
    first: (data.page?.number ?? 0) === 0,
    last: (data.page?.number ?? 0) >= (data.page?.totalPages ?? 1) - 1,
    empty: data.content.length === 0,
  }))
export type PaginatedVariants = z.infer<typeof paginatedVariantsSchema>

// ============================================================================
// Query Keys
// ============================================================================

export const MISSING_COST_QUERY_KEY = ['missing-cost', 'variants'] as const

// ============================================================================
// Service
// ============================================================================

/** Parameters for the missing-cost variants query. */
export interface MissingCostQueryParams {
  page: number
  size: number
}

/**
 * Fetch variants with no cost set (original_cost = 0), excluding system and
 * archived variants. Sorted by SKU ascending.
 *
 * @param params Pagination parameters.
 * @returns Normalized paginated variants.
 */
export async function fetchMissingCostVariants(
  params: MissingCostQueryParams
): Promise<PaginatedVariants> {
  const response = await apiClient.get('/variants', {
    params: {
      page: params.page,
      size: params.size,
      sort: 'sku',
      direction: 'asc',
      missingCost: true,
    },
  })
  return paginatedVariantsSchema.parse(response.data)
}

/** Payload for updating a single variant's cost. */
export interface UpdateVariantCostPayload {
  /** Variant id to update. */
  id: number
  /** New original cost (in the original currency). */
  originalCost: number
  /** 3-letter ISO currency code for the cost. */
  originalCurrency: string
  /** Date the cost is effective, as an ISO date string (yyyy-MM-dd). */
  costDate: string
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * React Query hook for the paginated list of variants missing a cost.
 *
 * @param params Pagination parameters.
 * @param options Additional query configuration overrides (e.g. `enabled`).
 * @returns The query result containing the paginated variants.
 */
export function useMissingCostQuery(
  params: MissingCostQueryParams,
  options?: { enabled?: boolean }
): UseQueryResult<PaginatedVariants, Error> {
  return useQuery<PaginatedVariants, Error>({
    queryKey: [...MISSING_COST_QUERY_KEY, params] as const,
    queryFn: () => fetchMissingCostVariants(params),
    placeholderData: previousData => previousData,
    enabled: options?.enabled ?? true,
  })
}

/**
 * Mutation hook for setting a variant's cost. On success the variant gains a
 * non-zero cost and drops off the missing-cost list, so we invalidate that
 * query plus the general variants cache.
 *
 * @returns Mutation result for the cost update.
 */
export function useUpdateVariantCostMutation(): UseMutationResult<
  Variant,
  Error,
  UpdateVariantCostPayload
> {
  const queryClient = useQueryClient()
  return useMutation<Variant, Error, UpdateVariantCostPayload>({
    mutationFn: ({ id, originalCost, originalCurrency, costDate }) => {
      const payload: UpdateVariantPayload = { originalCost, originalCurrency, costDate }
      return updateVariant(id, payload)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: MISSING_COST_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: ['variants'] })
    },
  })
}
